import { useRef, useState } from "react";
import api, { formatApiErrorDetail, BACKEND_URL } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Upload, X, GripVertical } from "lucide-react";

const resolveImg = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

const MAX_PHOTOS = 8;

/**
 * Reusable additional-photos picker.
 *
 * Used in two contexts:
 * - Inside the new-artwork form (uncontrolled-ish, just yields the array to onChange)
 * - Inside ManagePhotosDialog for editing an existing artwork
 *
 * Each row shows a thumbnail + a delete X. Click "+ Add photo" to upload another (max 8).
 */
export const AdditionalPhotosPicker = ({ value, onChange, disabled = false, testid = "extra-photos" }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handlePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (value.length >= MAX_PHOTOS) {
      toast.error(`Max ${MAX_PHOTOS} additional photos`);
      e.target.value = "";
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10MB)");
      e.target.value = "";
      return;
    }
    if (!/^image\/(png|jpe?g|webp|gif)$/.test(f.type)) {
      toast.error("Use PNG, JPEG, WebP or GIF");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange([...value, data.url]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeAt = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div data-testid={testid}>
      {value.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mt-3">
          {value.map((url, i) => (
            <div
              key={`${url}-${i}`}
              data-testid={`${testid}-item-${i}`}
              className="relative aspect-square border border-[#E5E0D8] bg-[#F4EFE6]"
            >
              <img
                src={resolveImg(url)}
                alt={`Detail ${i + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <button
                type="button"
                onClick={() => removeAt(i)}
                data-testid={`${testid}-remove-${i}`}
                aria-label="Remove photo"
                disabled={disabled}
                className="absolute top-1 right-1 bg-white/90 hover:bg-[#D32F2F] hover:text-white text-[#D32F2F] p-1 transition-colors border border-[#E5E0D8]"
              >
                <X className="w-3 h-3" strokeWidth={2} />
              </button>
              <span className="absolute bottom-1 left-1 label-eyebrow bg-white/90 text-[#1E3A8A] px-1.5 py-0.5 text-[10px] flex items-center gap-1">
                <GripVertical className="w-2.5 h-2.5" strokeWidth={1.5} />
                {i + 1}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <label
          htmlFor={`${testid}-file`}
          data-testid={`${testid}-add-label`}
          aria-disabled={uploading || disabled || value.length >= MAX_PHOTOS}
          className={`label-eyebrow border border-[#1E3A8A] text-[#1E3A8A] px-5 py-3 transition-colors ${
            uploading || disabled || value.length >= MAX_PHOTOS
              ? "opacity-40 cursor-not-allowed"
              : "cursor-pointer hover:bg-[#1E3A8A] hover:text-white"
          }`}
        >
          {uploading ? (
            <span className="inline-flex items-center gap-2">
              <Upload className="w-3 h-3 animate-pulse" strokeWidth={2} /> Uploading…
            </span>
          ) : (
            `+ Add photo (${value.length}/${MAX_PHOTOS})`
          )}
          <input
            id={`${testid}-file`}
            ref={fileRef}
            data-testid={`${testid}-file-input`}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={handlePick}
            className="hidden"
            disabled={uploading || disabled || value.length >= MAX_PHOTOS}
          />
        </label>
        <span className="text-xs text-[#555]">
          Up to {MAX_PHOTOS} extra photos (detail shots, scale, framing). JPEG/PNG/WebP up to 10MB.
        </span>
      </div>
    </div>
  );
};

export default AdditionalPhotosPicker;
